/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import ReadOnlyInputRow from '../../../components/ReadOnlyInputRow';
import { TextLink } from '../../../components/TextLink';
import { Grid } from '../../../components/Grid';
import List, { CardListItem } from '../../../components/List';
import { Caption } from '../../../components/Text';
import InsetRow from '../../../components/InsetRow';
import Spacer from '../../../components/Spacer';
import AppUtils from '../../../../utilities/AppUtils';
import RouteNames from '../../RouteNames';
import { LanguageWithCountryCode } from '../../../localization/LocalizedLanguage';
import { ScrollAwareContext, ScrollAwareScroller } from '../../../components/ScrollAware';
import ContentFrame from '../../../applicationFrame/ContentFrame';
import { AppHeaderScrollableText } from '../../../components/appNav/AppHeaderScrollable';
import { TwoFA } from './components/TwoFA';
import AppNavTopLevel from '../../../components/appNav/AppNavTopLevel';
import LogoutConfirmMessageDialog from '../../../components/MessageDialog/LogoutMessageDialog';
import ContentWrapper from '../../../components/ContentWrapper';

import './AccountSettings.styles.scss';
import { useActiveUser } from '../../../hooks/useUser';
import { useNavigate } from '../../../hooks/useNavigate';
import { useUIState } from '../../../hooks/useUIState';
import { Card } from '../../../../bphComponents/bundle';

export const AccountSettings: React.FC = () => {

  const [state, setState] = React.useState({
    isSendingEmail: false,
    loggingOut: false,
  });

  const emailMessage = state.isSendingEmail ? 'settings.resendingPendingEmail.button.label' : 'settings.resendPendingEmail.button.label';

  const [{ preferredLocale }, userActions] = useActiveUser();
  const user = userActions.instantiateUser();
  const profileId = user.profileId();
  const email = user.getEmail();
  const userData = user.data;
  const hasPendingEmail = user.hasPendingEmail();

  const [, navActions] = useNavigate();
  const [{ isActiveProfileMainProfile }, uiActions] = useUIState();

  React.useEffect(() => {
    if (isActiveProfileMainProfile) return;

    uiActions.setActiveProfile(profileId);
  }, []);

  return (
    <div>
      <ScrollAwareContext>
        <AppNavTopLevel hasDropShadow={true} />

        <ContentFrame className="bkgd-color-light-neutral">
          <ScrollAwareScroller>
            <AppHeaderScrollableText id="settings.user.title.text" />

            <ContentWrapper>

              <Card.CardShape>
                <List paddingSize="large" hasBorder>
                  <CardListItem
                    onClick={() => navActions.goTo(RouteNames.changeEmail())}
                    className={hasPendingEmail ? 'profile-background-color-10' : null}
                  >

                    <ReadOnlyInputRow
                      title={hasPendingEmail ? 'settings.pendingEmail.field.label' : 'settings.email.field.label'}
                      value={hasPendingEmail ? userData.pending_email : email}
                      style={{ wordBreak: 'break-all' }}
                    />

                    {
                      hasPendingEmail ? (
                        <Spacer size="20" topOnly>
                          <InsetRow>
                            <Grid justifyContent="space-between">
                              <TextLink
                                onClick={(e) => {
                                  e.stopPropagation();
                                  userActions.cancelPendingEmail();
                                }}
                              >
                                <Caption color="profile">
                                  <FormattedMessage id="settings.cancelPendingEmail.button.label" />
                                </Caption>
                              </TextLink>

                              <TextLink
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (state.isSendingEmail) { return; }

                                  setState({
                                    ...state,
                                    isSendingEmail: true,
                                  });

                                  userActions.verifyEmail()
                                    .then(() => {
                                      setState({
                                        ...state,
                                        isSendingEmail: false,
                                      });
                                    });
                                }}
                              >
                                <Caption color="profile">
                                  <FormattedMessage id={emailMessage} />
                                </Caption>
                              </TextLink>
                            </Grid>
                          </InsetRow>
                        </Spacer>
                      ) : null
                    }

                  </CardListItem>

                  <CardListItem
                    onClick={() => {
                      uiActions.startChangePasswordFlow();
                      navActions.goTo(RouteNames.changePassword());
                    }}
                  >
                    <ReadOnlyInputRow
                      title="settings.password.field.label"
                      value={<FormattedMessage id="settings.changePassword.button.label" />}
                    />
                  </CardListItem>

                  <CardListItem
                    onClick={() => {
                      if (user.isMfaEnabled()) {
                        navActions.goTo(RouteNames.mfaEnabled());
                      } else {
                        uiActions.startEnableMfaFlow();
                        navActions.goTo(RouteNames.mfaSetMobileNumber());
                      }
                    }}
                  >
                    <ReadOnlyInputRow
                      title="settings.2fa.field.label"
                      value={<TwoFA/>}
                    />
                  </CardListItem>

                  <CardListItem onClick={() => navActions.goTo(RouteNames.setPreferredLanguage())}>
                    <ReadOnlyInputRow
                      title="settings.language.field.label"
                      value={<LanguageWithCountryCode locale={preferredLocale} />}
                    />
                  </CardListItem>

                  <CardListItem onClick={() => navActions.goTo(RouteNames.communicationOptions())}>
                    <ReadOnlyInputRow
                      title="account.commsOptions.field.label"
                      value={<CommunicationOptionSummary userData={userData} />}
                    />
                  </CardListItem>
                </List>
              </Card.CardShape>

              <Card.CardShape
                onClick={() => {
                  if (AppUtils.isMobileDevice()) {
                    setState({
                      ...state,
                      loggingOut: true,
                    });
                  } else {
                    AppUtils.logout({}, AppUtils.reloadApp);
                  }
                }}
              >
                <Card.CardContent>
                  <span className="font-color-profile-accent">
                    <FormattedMessage id="list.appMenu.primary.logout.name"/>
                    {
                      state.loggingOut &&
                      <LogoutConfirmMessageDialog onCancel={() => setState({ ...state, loggingOut: false })}/>
                    }
                  </span>
                </Card.CardContent>
              </Card.CardShape>

              <Card.ExternalContent>
                <TextLink
                  className="font-caption-1 font-color-gsMedDark font-underline"
                  onClick={() => { navActions.goTo(RouteNames.deleteAccountConfirm()); }}
                >
                  <FormattedMessage id="subscription.deleteAccount.button.label" />
                </TextLink>
              </Card.ExternalContent>

            </ContentWrapper>

          </ScrollAwareScroller>
        </ContentFrame>
      </ScrollAwareContext>
    </div>
  );
};


const CommunicationOptionSummary = ({ userData }) => {
  const { allow_backpack_email_communication, allow_thirdparty_email_communication } = userData;

  if (allow_backpack_email_communication && allow_thirdparty_email_communication) {
    return <FormattedMessage id="ui-data.commsOptions.backpackNews3rdParty.text" />;
  } else if (allow_backpack_email_communication) {
    return <FormattedMessage id="ui-data.commsOptions.backpackNewsTips.text" />;
  } else {
    return <FormattedMessage id="ui-data.commsOptions.systemOnly.text" />;
  }
};
